import { useCallback, useMemo, useState } from 'react';
import { isObject, isString } from '../../utils';
export function isGroupItem(item) {
  return typeof item === 'object' && item !== null && 'items' in item;
}

/**
 * React hook that takes a list of items and a value to compare each one in the list against.
 * If item or property of item and comparison value are both strings then the builtin filter
 * function does a case-insensitive comparison. If this is not the case, then a
 * a strict equality (===) comparison is done instead.
 * A custom filter function can be supplied if a user requires a more complex filter.
 */
export var useFilter = _ref => {
  var {
    filter: filterProp,
    items,
    filterByValue,
    filterByProp = 'label'
  } = _ref;
  var [visibleItems, setVisibleItems] = useState(items);

  /**
   * Built-in filter function that checks an item against a value.
   * If both item or property of item and filterByValue are strings then a
   * case-insensitive comparison is done. If not then a strict equality (===) is
   * done instead.
   */
  var builtInFilter = useCallback((item, filterByValue) => {
    if (isString(filterByValue)) {
      if (isString(item)) {
        return item.toLowerCase().includes(filterByValue.toLowerCase());
      } else {
        var _item$filterByProp;
        return isObject(item) && ((_item$filterByProp = item[filterByProp]) === null || _item$filterByProp === void 0 ? void 0 : _item$filterByProp.toLowerCase().includes(filterByValue.toLowerCase()));
      }
    }
    return item[filterByProp] === filterByValue;
  }, [filterByProp]);
  var filter = filterProp !== null && filterProp !== void 0 ? filterProp : builtInFilter;
  var filterItems = useCallback(() => {
    var matchedItems = [];
    items.forEach(item => {
      if (isGroupItem(item)) {
        if (builtInFilter(item, filterByValue)) {
          matchedItems.push(item);
        } else {
          matchedItems.push(...item.items.filter(i => filter(i, filterByValue)));
        }
      } else {
        if (filter(item, filterByValue)) {
          matchedItems.push(item);
        }
      }
    });
    setVisibleItems(matchedItems);
  }, [builtInFilter, filterByValue, filter, items]);
  var clear = useCallback(() => {
    setVisibleItems(items);
  }, [items, setVisibleItems]);
  var count = useMemo(() => {
    var itemsCount = 0;
    visibleItems.forEach(i => {
      if (isGroupItem(i)) {
        itemsCount += i.items.length;
      } else {
        itemsCount += 1;
      }
    });
    return itemsCount;
  }, [visibleItems]);
  return {
    visibleItems,
    filterItems,
    count,
    clear
  };
};